import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ALLOWED_ANALYTICS_COUNTRIES, BLOCKED_COUNTRIES } from '@/constants/geo';

export interface GeoState {
  isChecked: boolean;
  isBlocked: boolean;
  countryCode: string | null;
  countryName: string | null;
}

interface GeoContextType {
  geoState: GeoState;
  setGeoState: (state: GeoState) => void;
  isAnalyticsAllowed: () => boolean;
}

const GeoContext = createContext<GeoContextType | null>(null);

export const GeoProvider = ({ children }: { children: ReactNode }) => {
  const [geoState, setGeoState] = useState<GeoState>({
    isChecked: false,
    isBlocked: false,
    countryCode: null,
    countryName: null,
  });

  const isAnalyticsAllowed = useCallback(() => {
    // Only allow analytics if:
    // 1. Geo check has completed
    // 2. User is NOT blocked
    // 3. User is from allowed country (US, CA, FR, NL, etc.)
    if (!geoState.isChecked) return false;
    if (geoState.isBlocked) return false;
    
    if (geoState.countryCode && !ALLOWED_ANALYTICS_COUNTRIES.includes(geoState.countryCode)) {
      return false;
    }
    
    return true;
  }, [geoState]);

  return (
    <GeoContext.Provider value={{ geoState, setGeoState, isAnalyticsAllowed }}>
      {children}
    </GeoContext.Provider>
  );
};

export const useGeo = (): GeoContextType => {
  const context = useContext(GeoContext);
  if (!context) {
    throw new Error('useGeo must be used within a GeoProvider');
  }
  return context;
};

// Export blocked countries for use elsewhere
export { BLOCKED_COUNTRIES };
